import { useEffect, useMemo, useRef, useState } from "react";
import { Printer, Search, Car, FileText, Wrench } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MONTHS_BM } from "../components/ButirSheets";
import { PenyelSheet, PENYEL_CSS, paginateRecords } from "../components/PenyelSheets";

const YEARS = [2024, 2025, 2026];
const SHEET_W = 1123;
const SHEET_H = 795;

export default function JanaPenyelenggaraan() {
  const [vehicles, setVehicles] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [month, setMonth] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const previewRef = useRef(null);
  const [scale, setScale] = useState(0.6);

  useEffect(() => { api.get("/vehicles").then((r) => setVehicles(r.data)); }, []);

  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, (el.clientWidth - 8) / SHEET_W));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [selected]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return vehicles.filter((v) => `${v.registration_number} ${v.make} ${v.model}`.toLowerCase().includes(q)).slice(0, 8);
  }, [query, vehicles]);

  useEffect(() => {
    if (!selected) { setData(null); return; }
    setLoading(true);
    const params = { year: Number(year) };
    if (month !== "all") params.month = Number(month);
    api.get(`/reports/penyelenggaraan/${selected.id}`, { params })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [selected, year, month]);

  const pages = useMemo(() => (data ? paginateRecords(data.records) : []), [data]);
  const monthQS = month !== "all" ? `&month=${month}` : "";

  return (
    <div className="grid lg:grid-cols-[340px_1fr] gap-5 items-start" data-testid="jana-penyelenggaraan-tab">
      <div className="space-y-5">
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-base">1. Pilih Kenderaan & Tempoh</CardTitle>
            <CardDescription>Rekod Penyelenggaraan Berjadual dijana bagi satu kenderaan.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5 relative">
              <Label>Kenderaan</Label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="penyel-vehicle-search" className="pl-9" placeholder="Cari nombor plat / model..."
                  value={query} onChange={(e) => { setQuery(e.target.value); setSelected(null); }} />
              </div>
              {results.length > 0 && !selected && (
                <div className="absolute z-50 top-full mt-1 inset-x-0 bg-white border rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="penyel-vehicle-results">
                  {results.map((v) => (
                    <button key={v.id} onClick={() => { setSelected(v); setQuery(v.registration_number); }}
                      data-testid={`penyel-vehicle-${v.registration_number}`}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b last:border-0">
                      <b>{v.registration_number}</b> — {v.make} {v.model} · {v.category}
                    </button>
                  ))}
                </div>
              )}
              {selected && (
                <div className="flex items-center gap-2 text-xs bg-indigo-50/70 border border-indigo-100 rounded-lg p-2.5">
                  <Car className="h-4 w-4 text-[#10056E]" />
                  <span><b>{selected.registration_number}</b> — {selected.make} {selected.model}</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tahun</Label>
                <Select value={year} onValueChange={setYear}>
                  <SelectTrigger data-testid="penyel-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Bulan</Label>
                <Select value={month} onValueChange={setMonth}>
                  <SelectTrigger data-testid="penyel-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="all">Semua Bulan</SelectItem>
                    {MONTHS_BM.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-base">2. Muat Turun</CardTitle>
            <CardDescription>Cetak atau simpan laporan sebagai PDF.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2.5">
            <Button disabled={!selected} className="w-full bg-[#10056E] hover:bg-[#2a17a8]" data-testid="penyel-pdf-btn"
              onClick={() => window.open(`/cetak/jana-penyelenggaraan/${selected.id}?year=${year}${monthQS}`, "_blank")}>
              <Printer className="h-4 w-4 mr-2" /> PDF (Cetak / Simpan PDF)
            </Button>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-base flex items-center gap-2">
              <Wrench className="h-4 w-4 text-[#10056E]" /> Bagaimana Data Dijana
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="penyel-explanation-panel">
            {[
              { k: "Tarikh & Bacaan Odometer", v: "Diambil daripada rekod status Penyelenggaraan kenderaan (odometer auto dari telematik)." },
              { k: "Butir-butir penyelenggaraan", v: "Butiran penyelenggaraan & pemeriksaan berkala yang diisi semasa menukar status kenderaan." },
              { k: "Pegawai Yang Mengesahkan", v: "Nama pegawai yang diisi pada borang penyelenggaraan. Ruang tandatangan dibiarkan untuk ditandatangan fizikal." },
              { k: "Jadual seterusnya", v: "Tarikh & odometer jadual penyelenggaraan seterusnya daripada rekod yang sama." },
            ].map((p) => (
              <div key={p.k} className="text-xs border-l-2 border-[#10056E]/30 pl-2.5">
                <b className="text-slate-800">{p.k}</b>
                <p className="text-muted-foreground mt-0.5 leading-relaxed">{p.v}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div ref={previewRef} className="min-w-0" data-testid="penyel-preview-area">
        {!selected && (
          <div className="border-2 border-dashed border-slate-200 rounded-xl bg-white/60 py-24 text-center">
            <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Pilih kenderaan untuk melihat pratonton halaman A4 sebenar.</p>
          </div>
        )}
        {selected && loading && <p className="text-sm text-muted-foreground py-16 text-center">Menjana pratonton...</p>}
        {selected && data && !loading && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <Badge variant="secondary" data-testid="penyel-badge-count">{data.total_records} rekod penyelenggaraan</Badge>
              <Badge variant="secondary">{pages.length} halaman A4</Badge>
            </div>
            {data.records.length === 0 && (
              <p className="text-xs text-muted-foreground bg-amber-50 border border-amber-200 rounded-lg p-3">
                Tiada rekod penyelenggaraan bagi tempoh ini — halaman kosong tetap dijana untuk diisi secara manual.
              </p>
            )}
            <style>{PENYEL_CSS}</style>
            <div className="penyel-doc space-y-4">
              {pages.map((rows, i) => (
                <div key={i} style={{ height: SHEET_H * scale, overflow: "hidden" }} data-testid={`penyel-sheet-${i}`}>
                  <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: SHEET_W }}>
                    <PenyelSheet data={data} rows={rows} footer={`M.S. ${i + 1}/${pages.length}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
